// src/pages/NotFound.tsx
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { 
  Home, 
  Search, 
  ArrowLeft,
  Trophy,
  Target,
  Sparkles,
  Navigation,
  AlertTriangle,
  MapPin,
  Compass,
  PhoneCall,
  Mail,
  MessageCircle,
  ExternalLink
} from 'lucide-react';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [countdown, setCountdown] = useState(10);
  const [autoRedirect, setAutoRedirect] = useState(true);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // Auto redirect countdown
  useEffect(() => {
    if (!autoRedirect) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      navigate('/');
    }
  }, [countdown, navigate, autoRedirect]);

  // Suggested pages based on common navigation
  const suggestedPages = [
    {
      icon: Home,
      title: { en: 'Home', ar: 'الرئيسية' },
      description: { en: 'Return to homepage', ar: 'العودة للصفحة الرئيسية' },
      path: '/',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Trophy,
      title: { en: 'About Us', ar: 'من نحن' },
      description: { en: 'Learn about our academy', ar: 'تعرف على الأكاديمية' },
      path: '/about',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Target,
      title: { en: 'Programs', ar: 'البرامج' },
      description: { en: 'Explore our sports programs', ar: 'استكشف برامجنا الرياضية' },
      path: '/programs',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Sparkles,
      title: { en: 'Contact', ar: 'اتصل بنا' },
      description: { en: 'Get in touch', ar: 'تواصل معنا' },
      path: '/contact',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  // Support options
  const supportOptions = [
    {
      icon: PhoneCall,
      text: { en: 'Call Support', ar: 'اتصل بالدعم' },
      href: '#'
    },
    {
      icon: Mail,
      text: { en: 'Email Us', ar: 'راسلنا' },
      href: 'mailto:info@championsacademy.ps'
    },
    {
      icon: MessageCircle,
      text: { en: 'Live Chat', ar: 'دردشة مباشرة' },
      href: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-surface-1 to-background flex items-center justify-center relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            rotate: [0, 180, 360],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-20 left-20 w-64 h-64 bg-primary/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            rotate: [360, 180, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 25, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl"
        />

        {/* Floating Sports Icons */}
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0],
          }}
          transition={{ duration: 5, repeat: Infinity }}
          className="absolute top-40 right-40 opacity-5"
        >
          <Trophy className="h-32 w-32 text-primary" />
        </motion.div>
        <motion.div
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0],
          }}
          transition={{ duration: 6, repeat: Infinity, delay: 1 }}
          className="absolute bottom-40 left-40 opacity-5"
        >
          <Target className="h-32 w-32 text-accent" />
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Error Message */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              {/* 404 Number */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.8, delay: 0.2 }}
                className="mb-8"
              >
                <div className="inline-flex items-center justify-center">
                  <motion.span
                    animate={{ 
                      textShadow: [
                        "0 0 20px rgba(var(--primary), 0.5)",
                        "0 0 40px rgba(var(--primary), 0.8)",
                        "0 0 20px rgba(var(--primary), 0.5)",
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-9xl md:text-[12rem] font-black gradient-text leading-none"
                  >
                    404
                  </motion.span>
                </div>
              </motion.div>

              {/* Error Icon */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-6"
              >
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-500/10 border border-orange-500/20">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <span className="text-sm font-semibold text-orange-500">
                    {isRTL ? 'الصفحة غير موجودة' : 'Page Not Found'}
                  </span>
                </div>
              </motion.div>

              {/* Title & Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-8"
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {isRTL ? 'عذراً! لقد ضللت الطريق' : 'Oops! You\'ve Lost Your Way'}
                </h1>
                <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
                  {isRTL
                    ? 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها. دعنا نساعدك في العودة إلى المسار الصحيح!'
                    : 'The page you\'re looking for doesn\'t exist or has been moved. Let\'s get you back on track!'
                  }
                </p>

                {/* Attempted Path */}
                <div className="p-4 rounded-xl bg-card border border-border inline-block">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span className="font-mono">{location.pathname}</span>
                  </div>
                </div>
              </motion.div>

              {/* Auto Redirect Timer */}
              {autoRedirect && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mb-8 p-6 rounded-2xl bg-primary/5 border border-primary/20"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <Compass className="h-6 w-6 text-primary" />
                      </motion.div>
                      <div>
                        <p className="text-sm font-semibold">
                          {isRTL ? 'إعادة التوجيه التلقائي' : 'Auto Redirect'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {isRTL 
                            ? `العودة للرئيسية في ${countdown} ثانية...`
                            : `Returning to home in ${countdown} seconds...`
                          }
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAutoRedirect(false)}
                    >
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button
                  size="lg"
                  className="btn-glow px-8 py-6 text-lg rounded-full"
                  onClick={() => navigate('/')}
                >
                  <Home className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'العودة للرئيسية' : 'Go Home'}
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 text-lg rounded-full border-2"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  {isRTL ? 'العودة للخلف' : 'Go Back'}
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 text-lg rounded-full border-2"
                  onClick={() => navigate('/search')}
                >
                  <Search className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'بحث' : 'Search'}
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Column - Suggested Pages */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {/* Suggested Pages */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Navigation className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">
                    {isRTL ? 'صفحات مقترحة' : 'Suggested Pages'}
                  </h2>
                </div>

                <div className="space-y-4">
                  {suggestedPages.map((page, idx) => (
                    <motion.div
                      key={page.path}
                      initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + idx * 0.1 }}
                      whileHover={{ x: isRTL ? -8 : 8, scale: 1.02 }}
                      onClick={() => navigate(page.path)}
                      className="group p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/50 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-4 rounded-xl bg-gradient-to-br ${page.color} bg-opacity-10 group-hover:scale-110 transition-transform duration-300`}>
                          <page.icon className="h-8 w-8 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold mb-1 group-hover:text-primary transition-colors">
                            {page.title[isRTL ? 'ar' : 'en']}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {page.description[isRTL ? 'ar' : 'en']}
                          </p>
                        </div>
                        <ExternalLink className={`h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors ${isRTL ? 'rotate-180' : ''}`} />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Support Options */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20"
              >
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" />
                  {isRTL ? 'تحتاج مساعدة؟' : 'Need Help?'}
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {supportOptions.map((option, idx) => (
                    <motion.a
                      key={idx}
                      href={option.href}
                      whileHover={{ scale: 1.05, y: -4 }}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-all duration-300 text-center"
                    >
                      <option.icon className="h-6 w-6 text-primary" />
                      <span className="text-xs font-medium">
                        {option.text[isRTL ? 'ar' : 'en']}
                      </span>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Fun Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-card border border-border shadow-lg">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              <p className="text-sm text-muted-foreground italic">
                {isRTL
                  ? 'حتى الأبطال يضلون الطريق أحياناً... المهم أن نعود أقوى! 💪'
                  : 'Even champions get lost sometimes... What matters is we come back stronger! 💪'
                }
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
